import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import {
  type AvailabilitySearch,
  parseAvailabilitySearch,
  parseSearchString,
  readSavedView,
} from "@/components/availability/search-params";
import { AvailabilityPage } from "@/routes/dashboard/availability";
import { PlanEditorPage } from "@/routes/dashboard/plans/$planId";
import { PlanOverviewPage } from "@/routes/dashboard/plans/$planId.overview";
import { DashboardPlanApprovalsPage } from "@/routes/dashboard/plans/approvals";
import { DashboardPlansPage } from "@/routes/dashboard/plans/index";
import { DemandLinksPanel } from "./components/integrations/DemandLinksPanel";
import { SyncPanel } from "./components/integrations/SyncPanel";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { IntegrationsLayout } from "./components/layout/IntegrationsLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { SETTINGS_TABS, visibleSettingsTabs } from "./lib/nav-access";
import {
  type DemandSearch,
  decodeDemandSearch,
  decodeRangeSearch,
  type RangeSearch,
} from "./lib/report-search";
import {
  requireAdmin,
  requireAuth,
  requireIntegrationsAccess,
  requireInternalRatesAccess,
  requireResourcesPageAccess,
} from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { AccountDetailPage } from "./routes/dashboard/accounts/$id";
import { AccountsPage } from "./routes/dashboard/accounts/index";
import { DemandsPage } from "./routes/dashboard/demands";
import { DashboardPage } from "./routes/dashboard/index";
import { IssueDetailPage } from "./routes/dashboard/issues/$id";
import { IssuesPage } from "./routes/dashboard/issues/index";
import { LogsPage } from "./routes/dashboard/logs";
import { NotificationsPage } from "./routes/dashboard/notifications";
import { ReportingPage } from "./routes/dashboard/reporting";
import { DemandByAccountPage } from "./routes/dashboard/reports/demand-by-account";
import {
  ResourceHoursIndexPage,
  ResourceHoursPage,
} from "./routes/dashboard/reports/resource-hours";
import { ResourcesPage } from "./routes/dashboard/resources";
import { SchedulePage } from "./routes/dashboard/schedule";
import { SettingsConstraintsPage } from "./routes/dashboard/settings/constraints";
import { SettingsDisciplinesPage } from "./routes/dashboard/settings/disciplines";
import { SettingsIntensityPage } from "./routes/dashboard/settings/intensity";
import { SettingsInternalRatesPage } from "./routes/dashboard/settings/internal-rates";
import { SettingsLaborCategoriesPage } from "./routes/dashboard/settings/labor-categories";
import { SettingsPlanningPage } from "./routes/dashboard/settings/planning";
import { SettingsPodCompositionPage } from "./routes/dashboard/settings/pod-composition";
import { SettingsRateCardPage } from "./routes/dashboard/settings/rate-card";
import { SettingsRequestPrioritiesPage } from "./routes/dashboard/settings/request-priorities";
import { SettingsRundownPage } from "./routes/dashboard/settings/rundown";
import { SettingsSignalsPage } from "./routes/dashboard/settings/signals";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsTiersPage } from "./routes/dashboard/settings/tiers";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { SettingsWorkstreamTemplatesPage } from "./routes/dashboard/settings/workstream-templates";
import { SignalsPage } from "./routes/dashboard/signals";
import { StaffingRequestDetailPage } from "./routes/dashboard/staffing-requests/$id";
import { StaffingRequestsPage } from "./routes/dashboard/staffing-requests/index";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    solutionId?: string;
    preset?: string;
    status?: string;
    fit?: string;
    pod?: string;
    intensity?: string;
    tier?: string;
    expectation?: string;
    constraints?: string;
    resource?: string;
  } => {
    const str = (v: unknown) => (typeof v === "string" && v.length > 0 ? v : undefined);
    return {
      solutionId: str(search.solutionId),
      preset: str(search.preset),
      status: str(search.status),
      fit: str(search.fit),
      pod: str(search.pod),
      intensity: str(search.intensity),
      tier: str(search.tier),
      expectation: str(search.expectation),
      constraints: str(search.constraints),
      resource: str(search.resource),
    };
  },
  component: function DashboardIndexRoute() {
    const { solutionId } = dashboardIndex.useSearch();
    return <DashboardPage initialSolutionId={solutionId} />;
  },
});

const resourcesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/resources",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string;
    disc?: string;
    con?: string;
    conMin?: string;
    load?: string;
    sol?: string;
    acct?: string;
    resv?: string;
  } => {
    const str = (v: unknown) => (typeof v === "string" && v.length > 0 ? v : undefined);
    return {
      q: str(search.q),
      disc: str(search.disc),
      con: str(search.con),
      conMin: str(search.conMin),
      load: str(search.load),
      sol: str(search.sol),
      acct: str(search.acct),
      resv: str(search.resv),
    };
  },
  // Not shown to a Solution Architect (2026-10-20); a direct link redirects to Solutions.
  beforeLoad: requireResourcesPageAccess,
  component: ResourcesPage,
});

const demandsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/demands",
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; status?: string; from?: string; to?: string; open?: string } => {
    const str = (v: unknown) => (typeof v === "string" && v.length > 0 ? v : undefined);
    return {
      q: str(search.q),
      status: str(search.status),
      from: str(search.from),
      to: str(search.to),
      open: str(search.open),
    };
  },
  component: DemandsPage,
});

const scheduleRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/schedule",
  component: SchedulePage,
});

const reportingRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reporting",
  component: ReportingPage,
});

const availabilityRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/availability",
  validateSearch: (search: Record<string, unknown>): AvailabilitySearch =>
    parseAvailabilitySearch(search),
  // A bare visit opens this browser's saved view ("Save view" writes it). Only on
  // entering the page, so returning to the default view with T is not undone.
  beforeLoad: ({ search, cause }) => {
    if (cause !== "enter" || Object.keys(search).length > 0) return;
    const saved = readSavedView();
    if (saved === null) return;
    const savedSearch = parseSearchString(saved);
    if (Object.keys(savedSearch).length === 0) return;
    throw redirect({ to: "/dashboard/availability", search: savedSearch, replace: true });
  },
  component: function AvailabilityRoute() {
    const search = availabilityRoute.useSearch();
    const navigate = availabilityRoute.useNavigate();
    return (
      <AvailabilityPage
        search={search}
        onSearchChange={(next) => navigate({ search: next, replace: true })}
      />
    );
  },
});

// ── Reports section (docs/specs/reports-hours.md) ────────────────────────────
// Hours reports. Every filter lives in the URL so a link reproduces the view; the API
// enforces who sees what (Demand by Account is Account-scoped, Resource Hours self-only).
const demandByAccountReportRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reports/demand",
  validateSearch: (search: Record<string, unknown>): DemandSearch => decodeDemandSearch(search),
  component: function DemandByAccountReportRoute() {
    const search = demandByAccountReportRoute.useSearch();
    const navigate = useNavigate();
    return (
      <DemandByAccountPage
        search={search}
        setSearch={(next) =>
          navigate({ to: "/dashboard/reports/demand", search: next, replace: true })
        }
      />
    );
  },
});

const resourceHoursIndexRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reports/resources",
  component: ResourceHoursIndexPage,
});

const resourceHoursRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reports/resources/$personId",
  validateSearch: (search: Record<string, unknown>): RangeSearch => decodeRangeSearch(search),
  component: function ResourceHoursRoute() {
    const { personId } = resourceHoursRoute.useParams();
    const search = resourceHoursRoute.useSearch();
    const navigate = useNavigate();
    return (
      <ResourceHoursPage
        key={personId}
        personId={personId}
        search={search}
        setSearch={(next) =>
          navigate({
            to: "/dashboard/reports/resources/$personId",
            params: { personId },
            search: next,
            replace: true,
          })
        }
      />
    );
  },
});

const staffingRequestsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/staffing-requests",
  component: StaffingRequestsPage,
});

// Every page is viewable by every signed-in role (product decision 2026-10-08), so Signals
// and Logs inherit dashboardLayout's requireAuth and nothing more. Logs stays open to SA
// too (2026-10-20): it has nothing to change. What a role can DO on
// them is still enforced by the API (processSolutionOnDemand, confirmSuggestedIntensity …).
const signalsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/signals",
  component: SignalsPage,
});

const logsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/logs",
  component: LogsPage,
});

// Reported Issues: everyone sees their own; the ADMIN queue is scoped by the API.
const issuesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/issues",
  component: IssuesPage,
});

// The signed-in user's own notices, full screen — opened from the bell in the status bar.
const notificationsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/notifications",
  component: NotificationsPage,
});

const issueDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/issues/$id",
  component: function IssueDetailRoute() {
    const { id } = issueDetailRoute.useParams();
    return <IssueDetailPage id={id} />;
  },
});

const staffingRequestDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/staffing-requests/$id",
  component: function StaffingRequestDetailRoute() {
    const { id } = staffingRequestDetailRoute.useParams();
    return <StaffingRequestDetailPage id={id} />;
  },
});

const accountsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/accounts",
  component: AccountsPage,
});

const accountDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/accounts/$id",
  component: function AccountDetailRoute() {
    const { id } = accountDetailRoute.useParams();
    return <AccountDetailPage id={id} />;
  },
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

// ── Resource Plan M2 — Plan on a Solution (docs/specs/resource-plan.md) ──────
// The grid needs a page of its own: Solution Detail is a drawer capped at 840px, and 27
// months at 62px plus a 614px frozen block is ~2,288px of table.
const plansRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/plans",
  component: DashboardPlansPage,
});

/*
 * Epic 17's approver queue. Declared BEFORE the `$planId` route so the static segment
 * wins: "approvals" is otherwise a perfectly good plan id as far as the matcher is
 * concerned. Gated server-side on `viewPlanCost` — every row carries a shortfall in
 * dollars — and hidden from the sidebar for the roles that lack it.
 */
const planApprovalsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/plans/approvals",
  component: DashboardPlanApprovalsPage,
});

const planEditorRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/plans/$planId",
  validateSearch: (search: Record<string, unknown>): { tab?: "changes" } =>
    search.tab === "changes" ? { tab: "changes" } : {},
  component: function PlanEditorRoute() {
    const { planId } = planEditorRoute.useParams();
    const { tab } = planEditorRoute.useSearch();
    return <PlanEditorPage key={`${planId}:${tab ?? ""}`} planId={planId} initialTab={tab} />;
  },
});

/**
 * The read-only, shareable Plan Overview — a sibling of the editor route, not a child
 * of it, so it inherits `requireAuth` from `dashboardLayout` without inheriting any of
 * the editor's own state or affordances.
 */
const planOverviewRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/plans/$planId/overview",
  component: function PlanOverviewRoute() {
    const { planId } = planOverviewRoute.useParams();
    return <PlanOverviewPage planId={planId} />;
  },
});

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  // Open to every signed-in role: what a role cannot change is rendered view-only by
  // SettingsLayout and refused by the API. The exceptions are the tabs nav-access.ts hides
  // (Users, Internal Rates, and for SA every tab it can only read — 2026-10-20): the bare
  // path and a hidden tab both land on the role's first visible tab.
  beforeLoad: async (ctx) => {
    const session = await requireAuth();
    const tabs = visibleSettingsTabs(session.user.role);
    const first = tabs[0];
    if (first === undefined) throw redirect({ to: "/dashboard" });
    const pathname = ctx.location.pathname.replace(/\/+$/, "");
    const hidden = SETTINGS_TABS.some(
      (tab) => tab.href === pathname && !tabs.some((visible) => visible.href === tab.href),
    );
    if (pathname === "/dashboard/settings" || hidden) throw redirect({ to: first.href });
  },
  component: SettingsLayoutRoute,
});

// User administration (roles, passwords, sessions) stays Admin/RM only.
const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  beforeLoad: requireAdmin,
  component: SettingsUsersPage,
});

const settingsDisciplinesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/disciplines",
  component: SettingsDisciplinesPage,
});

const settingsPodCompositionRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/pod-composition",
  component: SettingsPodCompositionPage,
});

// Settings ▸ Tiers — the one screen an Engagement Standard Tier is configured from.
const settingsTiersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/tiers",
  component: SettingsTiersPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsConstraintsRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/constraints",
  component: SettingsConstraintsPage,
});

const settingsRequestPrioritiesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/request-priorities",
  component: SettingsRequestPrioritiesPage,
});

const settingsIntensityRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/intensity",
  component: SettingsIntensityPage,
});

// Integrations moved out of Settings to its own left-sidebar page. Keep this
// route as a redirect so old /dashboard/settings/integrations links keep working.
const settingsIntegrationsRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/integrations",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/integrations/sync" });
  },
});

// ── Resource Plan M1 — Configuration spine (docs/specs/resource-plan.md) ──
// One tab per epic: 11 (Labor Category Catalog), 12 (Rate Card), 14 (Planning Constants).
const settingsLaborCategoriesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/labor-categories",
  component: SettingsLaborCategoriesPage,
});

const settingsRateCardRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/rate-card",
  component: SettingsRateCardPage,
});

// Resource-level cost (2026-10-14). The tab is ADMIN / FIN / RM (RM read-only, 2026-10-15) and the page asks for a
// step-up re-authentication on every visit; the API enforces both.
const settingsInternalRatesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/internal-rates",
  beforeLoad: requireInternalRatesAccess,
  component: SettingsInternalRatesPage,
});

const settingsPlanningRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/planning",
  component: SettingsPlanningPage,
});

// Resource Plan M3 — Epic 13. Configuration, per §12: `manageConfiguration` (ADMIN/RM),
// not `manageRateCard` — a Workstream Template is a delivery-shape artefact, not [FIN]'s.
const settingsWorkstreamTemplatesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/workstream-templates",
  component: SettingsWorkstreamTemplatesPage,
});

const settingsRundownRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/rundown",
  component: SettingsRundownPage,
});

const settingsSignalsRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/signals",
  component: SettingsSignalsPage,
});

// ── Integrations (top-level, admin-gated) ─────────────────────────────────────
// A dedicated admin layout route (guard on the layout, not on leaves — per the
// frontend routing rule) so Integrations lives in the left sidebar with its own
// tab strip rather than inside the Settings panel.
function IntegrationsLayoutRoute() {
  return (
    <IntegrationsLayout>
      <Outlet />
    </IntegrationsLayout>
  );
}

const integrationsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/integrations",
  beforeLoad: async (ctx) => {
    // Hidden from a role that can neither refresh nor import (SA, 2026-10-20).
    await requireIntegrationsAccess();
    if (ctx.location.pathname === "/dashboard/integrations") {
      throw redirect({ to: "/dashboard/integrations/sync" });
    }
  },
  component: IntegrationsLayoutRoute,
});

const integrationsSyncRoute = createRoute({
  getParentRoute: () => integrationsLayout,
  path: "/sync",
  component: SyncPanel,
});

const integrationsDemandsRoute = createRoute({
  getParentRoute: () => integrationsLayout,
  path: "/demands",
  component: DemandLinksPanel,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    resourcesRoute,
    demandsRoute,
    scheduleRoute,
    reportingRoute,
    availabilityRoute,
    demandByAccountReportRoute,
    resourceHoursIndexRoute,
    resourceHoursRoute,
    staffingRequestsRoute,
    staffingRequestDetailRoute,
    signalsRoute,
    logsRoute,
    issuesRoute,
    notificationsRoute,
    issueDetailRoute,
    accountsRoute,
    accountDetailRoute,
    plansRoute,
    planApprovalsRoute,
    planEditorRoute,
    planOverviewRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsDisciplinesRoute,
      settingsPodCompositionRoute,
      settingsTiersRoute,
      settingsConstraintsRoute,
      settingsIntensityRoute,
      settingsRequestPrioritiesRoute,
      settingsSystemRoute,
      settingsIntegrationsRoute,
      settingsWorkstreamTemplatesRoute,
      settingsRundownRoute,
      settingsSignalsRoute,
      settingsLaborCategoriesRoute,
      settingsRateCardRoute,
      settingsInternalRatesRoute,
      settingsPlanningRoute,
    ]),
    integrationsLayout.addChildren([integrationsSyncRoute, integrationsDemandsRoute]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
